/* eslint-disable @reatom/unit-naming-rule */
import { action, atom, onConnect, withAssign } from '@reatom/framework'
import * as DateF from 'date-fns'

import type { CtxSpy } from '@reatom/framework'

export const reatomDateComputed = (
  compute: (ctx: CtxSpy) => Date,
  name?: string,
) => {
  const referenceDateAtom = atom(new Date())
  const dateAtom = atom(compute, name)

  const isPastAtom = atom((ctx) => {
    const referenceDate = ctx.spy(referenceDateAtom)
    const date = ctx.get(dateAtom)
    return DateF.isBefore(date, referenceDate)
  })

  const isFutureAtom = atom((ctx) => {
    return !ctx.spy(isPastAtom)
  })

  onConnect(isPastAtom, (ctx) => {
    let currentTimeout: number | undefined

    const installTimeoutAction = action((ctx) => {
      if (currentTimeout) {
        clearTimeout(currentTimeout)
      }
      const date = ctx.get(dateAtom)
      const now = new Date()
      referenceDateAtom(ctx, now)

      const diff = DateF.differenceInMilliseconds(date, now)
      if (diff <= 0) {
        return () => {}
      }

      currentTimeout = window.setTimeout(() => {
        referenceDateAtom(ctx, new Date())
      }, diff + 100)
    })

    const unsub = dateAtom.onChange(installTimeoutAction)
    installTimeoutAction(ctx)

    return () => {
      if (currentTimeout) {
        clearTimeout(currentTimeout)
      }
      unsub()
    }
  })

  return dateAtom.pipe(
    withAssign(() => ({
      isPast: isPastAtom,
      isFuture: isFutureAtom,
    })),
  )
}

export const reatomDate = (init: Date) => {
  const sourceAtom = atom(init)

  const dateAtom = reatomDateComputed((ctx) => {
    return ctx.spy(sourceAtom)
  }, 'reatom-date')

  const setAction = action((ctx, date: Date) => {
    sourceAtom(ctx, date)
  })

  return dateAtom.pipe(
    withAssign(() => ({
      set: setAction,
    })),
  )
}
